// Read-only matrix used in the admin view.
// Two modes:
//   - Aggregate (default): each slot is coloured by its yes-quote across all voters.
//   - Spotlight: when a single voter is selected, each slot shows that voter's
//     yes/maybe/no/no-answer in voter-view colours.

import type { Appointment, FinderConfig } from '../types';
import type { SurveyVote } from '../api';
import { enumerateDates, formatDateShort, formatTime, fromISODate } from '../time';
import { holidaysForCountries, holidayFlagsForCountries } from '../holidays';
import { laneCountForDate, slotCount, slotStartMinutes, zoneForMinutes } from '../logic';
import { el } from './dom';

const TIME_COLUMN_WIDTH = '6.5rem';
const MIN_CELL_WIDTH = 'var(--matrix-min-cell, 2rem)';

type SlotStats = {
  yes: number;
  maybe: number;
  no: number;
  total: number;
  score: number | null;
};

export function renderAdminMatrix(
  config: FinderConfig,
  slots: Appointment[],
  votes: SurveyVote[],
  spotlight: SurveyVote | null = null,
): HTMLElement {
  const root = el('section', { class: 'admin-matrix-panel', 'aria-label': 'Heat-Map' });

  const heading = el('div', { class: 'admin-matrix-heading' });
  if (spotlight) {
    heading.append(
      el('h2', { class: 'admin-section-heading' }, 'Antworten von '),
    );
    heading.firstElementChild!.append(
      el('span', { class: 'admin-spotlight-name' }, `„${spotlight.pseudonym}"`),
    );
    heading.append(
      el('p', { class: 'admin-matrix-hint' },
        'Jeder Slot zeigt die Antwort dieses Teilnehmers. ',
        'Klicke unten erneut auf den Namen oder „Zurück zur Übersicht", um wieder die Aggregat-Sicht zu sehen.',
      ),
    );
  } else {
    heading.append(
      el('h2', { class: 'admin-section-heading' }, 'Übersicht'),
      el('p', { class: 'admin-matrix-hint' },
        'Farbe entspricht der Ja-Quote (grün = viele Zusagen, rot = viele Absagen, grau = keine Stimmen). ',
        'Klicke unten auf einen Teilnehmer, um nur dessen Antworten zu sehen.',
      ),
    );
  }
  root.append(heading);

  const cols = slotCount(config);
  const dates = enumerateDates(config.rangeStart, config.rangeEnd, config.allowedWeekdays);
  if (cols <= 0 || dates.length === 0 || slots.length === 0) {
    root.append(el('div', { class: 'matrix-empty' }, 'Keine Termine zum Anzeigen.'));
    return root;
  }

  const stats = computeStats(slots, votes);
  const bestId = spotlight ? null : findBestSlotId(stats);

  const grid = el('div', { class: 'matrix-grid' });
  grid.style.gridTemplateColumns = `${TIME_COLUMN_WIDTH} repeat(${cols}, minmax(${MIN_CELL_WIDTH}, 1fr))`;
  root.append(grid);

  const laneCounts = dates.map((d) => laneCountForDate(slots, d));
  const rowOffsets: number[] = [];
  let cursor = 2;
  for (let i = 0; i < dates.length; i++) {
    rowOffsets.push(cursor);
    cursor += laneCounts[i];
  }

  const holidayMap = config.holidayCountries.length > 0
    ? holidaysForCountries(config.holidayCountries, config.rangeStart, config.rangeEnd)
    : new Map<string, string>();
  const holidayFlagsMap = config.holidayCountries.length > 0
    ? holidayFlagsForCountries(config.holidayCountries, config.rangeStart, config.rangeEnd)
    : new Map<string, string>();

  // Header (sticky elements span full axis — see matrix.ts comment).
  const corner = el('div', { class: 'matrix-corner' });
  corner.style.gridRow = '1 / -1';
  corner.style.gridColumn = '1 / -1';
  grid.append(corner);
  for (let c = 0; c < cols; c++) {
    const minutes = slotStartMinutes(config, c);
    const showLabel = config.slotSizeMinutes >= 30 || minutes % 60 === 0;
    const header = el(
      'div',
      { class: `matrix-col-header zone-${zoneForMinutes(minutes)}` },
      showLabel ? formatTime(minutes) : '',
    );
    header.style.gridRow = '1 / -1';
    header.style.gridColumn = String(c + 2);
    grid.append(header);
  }

  // Day rows
  dates.forEach((date, dateIdx) => {
    const weekday = fromISODate(date).getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    const holidayName = holidayMap.get(date);
    const laneCount = laneCounts[dateIdx];
    const dayStartRow = rowOffsets[dateIdx];

    const rowLabelWrap = el('div', { class: 'matrix-row-label-wrap' });
    rowLabelWrap.style.gridRow = `${dayStartRow} / ${dayStartRow + laneCount}`;
    rowLabelWrap.style.gridColumn = '1 / -1';

    const rowLabel = el('div', { class: 'matrix-row-label' });
    rowLabel.append(el('span', { class: 'row-date' }, formatDateShort(date)));
    const holidayFlags = holidayFlagsMap.get(date);
    if (holidayFlags) {
      const flagSpan = el('span', { class: 'row-holiday' });
      flagSpan.append(el('span', { class: 'row-holiday-flag' }, holidayFlags));
      if (holidayName) {
        flagSpan.title = holidayName;
        flagSpan.append(el('span', { class: 'row-holiday-name' }, holidayName));
      }
      rowLabel.append(flagSpan);
    }
    if (isWeekend) rowLabel.classList.add('is-weekend');
    if (holidayName) rowLabel.classList.add('is-holiday');
    if (dateIdx > 0) rowLabel.classList.add('is-day-divider');
    rowLabelWrap.append(rowLabel);
    grid.append(rowLabelWrap);

    for (let lane = 0; lane < laneCount; lane++) {
      for (let c = 0; c < cols; c++) {
        const cell = el('div', { class: 'matrix-cell admin-readonly-cell' });
        cell.style.gridRow = String(dayStartRow + lane);
        cell.style.gridColumn = String(c + 2);
        if (isWeekend) cell.classList.add('is-weekend');
        if (holidayName) cell.classList.add('is-holiday');
        if (lane > 0) cell.classList.add('is-sublane');
        if (lane === 0 && dateIdx > 0) cell.classList.add('is-day-top');
        grid.append(cell);
      }
    }
  });

  // Slot overlays
  for (const slot of slots) {
    const dateIdx = dates.indexOf(slot.date);
    if (dateIdx < 0) continue;
    const absRow = rowOffsets[dateIdx] + slot.lane;
    const overlay = spotlight
      ? renderSpotlightOverlay(config, slot, absRow, spotlight)
      : renderAggregateOverlay(config, slot, absRow, stats.get(slot.id), slot.id === bestId);
    if (overlay) grid.append(overlay);
  }

  return root;
}

function gridPosition(
  config: FinderConfig,
  slot: Appointment,
): { startCol: number; spanCols: number } | null {
  const startCol = (slot.startMinutes - config.dayStartMinutes) / config.slotSizeMinutes;
  const spanCols = (slot.endMinutes - slot.startMinutes) / config.slotSizeMinutes;
  if (!Number.isFinite(startCol) || !Number.isFinite(spanCols) || spanCols <= 0) return null;
  return { startCol: Math.round(startCol), spanCols: Math.round(spanCols) };
}

function renderAggregateOverlay(
  config: FinderConfig,
  slot: Appointment,
  gridRow: number,
  stats: SlotStats | undefined,
  isBest: boolean,
): HTMLElement | null {
  const pos = gridPosition(config, slot);
  if (!pos) return null;

  const overlay = el('div', { class: 'admin-stats-overlay' });
  overlay.style.gridRow = String(gridRow);
  overlay.style.gridColumn = `${pos.startCol + 2} / ${pos.startCol + 2 + pos.spanCols}`;

  const score = stats?.score ?? null;
  if (score === null) {
    overlay.classList.add('no-votes');
  } else {
    const hue = Math.round(score * 120);
    overlay.style.background = `hsla(${hue}, 65%, 55%, 0.72)`;
    overlay.style.borderColor = `hsl(${hue}, 65%, 35%)`;
    overlay.style.color = score > 0.55 ? '#0f3a18' : '#5b0d0d';
  }
  if (isBest) overlay.classList.add('is-best');

  if (stats && stats.total > 0) {
    overlay.append(
      el('span', { class: 'admin-stats-line' },
        `${stats.yes}✓ ${stats.maybe}? ${stats.no}✗`),
    );
  } else {
    overlay.append(el('span', { class: 'admin-stats-line' }, '—'));
  }
  return overlay;
}

function renderSpotlightOverlay(
  config: FinderConfig,
  slot: Appointment,
  gridRow: number,
  vote: SurveyVote,
): HTMLElement | null {
  const pos = gridPosition(config, slot);
  if (!pos) return null;

  const choice = vote.responses[slot.id];
  const overlay = el('div', { class: 'admin-spotlight-overlay' });
  overlay.style.gridRow = String(gridRow);
  overlay.style.gridColumn = `${pos.startCol + 2} / ${pos.startCol + 2 + pos.spanCols}`;

  let symbol = '—';
  let label: 'is-yes' | 'is-maybe' | 'is-no' | 'is-empty' = 'is-empty';
  let labelText = 'keine Antwort';
  switch (choice) {
    case 'yes':   label = 'is-yes';   symbol = '✓'; labelText = 'kann'; break;
    case 'maybe': label = 'is-maybe'; symbol = '?'; labelText = 'vielleicht'; break;
    case 'no':    label = 'is-no';    symbol = '✗'; labelText = 'nein'; break;
  }
  overlay.classList.add(label);
  overlay.title =
    `${labelText} · ${formatTime(slot.startMinutes)}–${formatTime(slot.endMinutes)}`;

  overlay.append(
    el('span', { class: 'admin-spotlight-symbol' }, symbol),
    el('span', { class: 'admin-spotlight-time' },
      `${formatTime(slot.startMinutes)}–${formatTime(slot.endMinutes)}`,
    ),
  );
  return overlay;
}

function computeStats(slots: Appointment[], votes: SurveyVote[]): Map<string, SlotStats> {
  const map = new Map<string, SlotStats>();
  for (const slot of slots) {
    map.set(slot.id, { yes: 0, maybe: 0, no: 0, total: 0, score: null });
  }
  for (const vote of votes) {
    for (const [slotId, choice] of Object.entries(vote.responses)) {
      const stats = map.get(slotId);
      if (!stats) continue;
      stats.total++;
      if (choice === 'yes') stats.yes++;
      else if (choice === 'maybe') stats.maybe++;
      else if (choice === 'no') stats.no++;
    }
  }
  // Heat-map colour represents the in-slot quality (% positive of those who
  // answered) — that's still useful as a saturation cue.
  for (const stats of map.values()) {
    if (stats.total === 0) continue;
    stats.score = (stats.yes + 0.5 * stats.maybe) / stats.total;
  }
  return map;
}

/** The "best slot" for the ★ marker is now defined by absolute positive count
 *  (4 yes beats 2 yes regardless of percentage), tie-broken by fewer no's. */
function findBestSlotId(stats: Map<string, SlotStats>): string | null {
  let bestId: string | null = null;
  let bestPositive = -1;
  let bestNoCount = Infinity;
  for (const [id, s] of stats) {
    if (s.total === 0) continue;
    const positive = s.yes + 0.5 * s.maybe;
    if (positive > bestPositive ||
        (positive === bestPositive && s.no < bestNoCount)) {
      bestPositive = positive;
      bestNoCount = s.no;
      bestId = id;
    }
  }
  return bestId;
}
